/**
 * UI translations. The English text is the key: t('My View') returns the text in the current language,
 * or the English text itself when there's no translation. Placeholders: t('Updated {date}', { date }).
 * Plurals: tp(n, '{n} member', '{n} members') picks the right form (Ukrainian and Polish have several).
 */
import { useSyncExternalStore } from 'react';
import de from './locales/de';
import pl from './locales/pl';
import uk from './locales/uk';

export type Lang = 'en' | 'de' | 'uk' | 'pl';

/** A translation: plain text, or plural forms by Intl.PluralRules category. */
export type Entry = string | Partial<Record<Intl.LDMLPluralRule, string>>;
export type Dict = Record<string, Entry>;

export const LANGUAGES: { code: Lang; label: string; short: string }[] = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'de', label: 'Deutsch', short: 'DE' },
  { code: 'uk', label: 'Українська', short: 'UA' },
  { code: 'pl', label: 'Polski', short: 'PL' },
];

/** English only needs the keys that carry a context marker ("text|context"). */
const en: Dict = { 'Summary|page': 'Summary' };

const DICTS: Record<Lang, Dict> = { en, de, uk, pl };
const STORAGE_KEY = 'bt.lang';

const isLang = (v: unknown): v is Lang => typeof v === 'string' && v in DICTS;

function stored(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLang(v) ? v : 'en';
  } catch {
    return 'en';
  }
}

let current: Lang = stored();
const listeners = new Set<() => void>();
if (typeof document !== 'undefined') document.documentElement.lang = current;

export const getLanguage = () => current;

/** Switches the UI language (re-renders everything that uses useLanguage). */
export function setLanguage(lang: string | null | undefined): void {
  if (!isLang(lang) || lang === current) return;
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* storage unavailable */
  }
  document.documentElement.lang = lang;
  for (const l of listeners) l();
}

export function useLanguage(): Lang {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    getLanguage,
  );
}

const fill = (s: string, vars?: Record<string, string | number | null | undefined>) =>
  vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m)) : s;

export function t(text: string, vars?: Record<string, string | number | null | undefined>): string {
  const e = DICTS[current][text];
  const s = typeof e === 'string' ? e : (e?.other ?? text);
  return fill(s, vars);
}

/** Plural: `one` / `other` are the English forms; {n} is replaced by the number. */
export function tp(n: number, one: string, other: string, vars?: Record<string, string | number | null | undefined>): string {
  const e = DICTS[current][other] ?? DICTS[current][one];
  const cat = new Intl.PluralRules(current).select(n);
  let s: string;
  if (e && typeof e === 'object') s = e[cat] ?? e.other ?? (n === 1 ? one : other);
  else if (typeof e === 'string') s = e;
  else s = n === 1 ? one : other;
  return fill(s, { n, ...vars });
}

/** Intl locale for dates and numbers. */
export const locale = () => ({ en: 'en-GB', de: 'de-DE', uk: 'uk-UA', pl: 'pl-PL' })[current];

/** Marks text for translation without translating it yet (for constants; translate with t() when shown). */
export const N_ = (text: string) => text;

/** Translates an error message from the server; also handles "field: message" and "a; b" (validation errors). */
export function tError(msg: string): string {
  if (!msg) return msg;
  if (DICTS[current][msg] !== undefined) return t(msg);
  return msg
    .split('; ')
    .map((part) => {
      const m = /^([\w.]+): (.+)$/.exec(part);
      return m && DICTS[current][m[2]] !== undefined ? t(m[2]) : t(part);
    })
    .join('; ');
}
